import React from "react";
import { DollarSign, Briefcase, Users, Receipt, Activity } from "lucide-react";
import { useNexOs, formatKpi } from "@/lib/nexOs";
import { useLabel, Section } from "@/lib/dynamicEngine";
import { useNdsEditor, useTileHandlers } from "@/lib/ndsEditor";
import { useFloatingWindows } from "@/lib/floatingWindows";
import { DeskPeriodBar } from "@/components/nds/PeriodToggle";

const COLS = { 4: "xl:grid-cols-4", 5: "xl:grid-cols-5" };

const ICONS = {
  dollar: DollarSign,
  briefcase: Briefcase,
  users: Users,
  receipt: Receipt,
  default: Activity,
};

/**
 * Tier 1 — Real-Time KPI Pulse Bar.
 * 4 uniform metric cards with an icon badge in the top-right of each card.
 * Every card is wrapped in <Section> so it can be individually hidden/reordered
 * from /admin/layout without a code change. In Edit Mode, single-click opens
 * the Quick-Edit modal; double-click escalates to the Workbench overlay.
 */
const PULSE_VERTICALS = ["all_engines", "resident_engine", "partner_engine"];  // these used the old-platform brief placeholders

export default function KpiPulseBar({ ticker = null, pulse = null }) {
  const { kpis, currentVertical, finPeriod, switching } = useNexOs();
  const { openFinReport } = useFloatingWindows();
  const finToggle = !!finPeriod && currentVertical?.id === "finance_engine";
  const rg = kpis[0]?.range;
  const showPulse = !!pulse && PULSE_VERTICALS.includes(currentVertical?.id);
  const kicker = useLabel("nds.kpi.kicker", "// REAL-TIME KPI PULSE");
  const liveTag = useLabel("nds.kpi.live_tag", "LIVE");
  const demoTag = useLabel("nds.kpi.demo_tag", "DEMO");

  return (
    <div className="flex flex-col gap-4" data-testid="nds-kpi-bar">
      <div className="flex items-baseline justify-between">
        <div className="nds-label-dim" data-testid="nds-kpi-kicker">{showPulse ? "// RESIDENT AND PROGRAM PULSE" : kicker}</div>
        {currentVertical && (
          <div className="font-mono text-[0.7rem] text-nex-chrome tracking-[0.18em] uppercase">
            {currentVertical.label}
          </div>
        )}
      </div>
      {finToggle && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2" data-testid="nds-pulse-period">
          <DeskPeriodBar rg={rg} busy={switching} testid="nds-pulse-period" />
          <span className="text-[0.6rem] text-nex-muted" data-testid="nds-pulse-period-note">Grants tiles and Cash in Hand keep their own windows.</span>
          {rg && <button type="button" onClick={() => openFinReport({ ...rg.report, title: `Finance Report · ${rg.label}` })} data-testid="nds-pulse-open-report"
            className="ml-auto font-mono text-[0.6rem] tracking-[0.18em] uppercase text-nex-gold hover:underline">Open in Finance Report</button>}
        </div>
      )}
      {ticker}
      {showPulse ? pulse : <div className={`grid grid-cols-1 sm:grid-cols-2 ${COLS[kpis.length] || "xl:grid-cols-4"} gap-4`}>
        {kpis.map((k) => {
          const Icon = ICONS[k.icon] || ICONS.default;
          const target = currentVertical ? { kind: "kpi", verticalId: currentVertical.id, itemId: k.id } : null;
          return (
            <Section scope="nds_kpi" id={k.id} key={k.id}>
              <KpiCard k={k} Icon={Icon} target={target} liveTag={liveTag} demoTag={demoTag} dim={finToggle && switching} />
            </Section>
          );
        })}
        {kpis.length === 0 && (
          <div className="col-span-4 nds-card p-8 text-center font-mono text-sm text-nex-chrome" data-testid="nds-kpi-empty">
            Loading KPIs…
          </div>
        )}
      </div>}
    </div>
  );
}

function KpiCard({ k, Icon, target, liveTag, demoTag, dim }) {
  const { openTileEditor } = useNdsEditor();
  const tileHandlers = useTileHandlers({ target, onEdit: openTileEditor });
  const { openWindow } = useFloatingWindows();
  const act = k.action;
  const open = act ? () => openWindow(act.window, act.payload || {}) : undefined;
  return (
    <div
      {...tileHandlers}
      onClick={open}
      onKeyDown={open ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); open(); } } : undefined}
      role={open ? "button" : undefined}
      tabIndex={open ? 0 : undefined}
      className={`nds-card p-5 h-full relative transition-all duration-200 ${open ? "cursor-pointer hover:border-nex-violet" : ""}`}
      data-testid={`nds-kpi-card-${k.id}`}
      title={k.hint || "Right-click to edit"}
    >
      <div className="flex items-start justify-between">
        <div className="nds-label">{k.label}</div>
        <div className="w-9 h-9 grid place-items-center bg-nex-navy text-nex-goldbright rounded-sm">
          <Icon className="w-4 h-4" strokeWidth={1.8} />
        </div>
      </div>
      <div
        className={`mt-4 text-3xl md:text-4xl nds-num tabular-nums transition-opacity ${dim ? "opacity-40 animate-pulse" : ""}`}
        data-testid={`nds-kpi-value-${k.id}`}
      >
        {formatKpi(k.value, k.format)}
      </div>
      <div className="mt-3 flex items-center gap-2">
        <span
          className={`nds-pill ${k.is_live ? "nds-pill--live" : "nds-pill--queued"}`}
          style={{ padding: "3px 8px", fontSize: "0.625rem" }}
          data-testid={`nds-kpi-tag-${k.id}`}
        >
          {k.is_live ? liveTag : demoTag}
        </span>
        <span className="font-mono text-[0.65rem] text-nex-muted tracking-widest uppercase">
          · {k.range && k.range.period !== "30d" ? k.range.label : "trailing 30d"}
        </span>
      </div>
      {(k.sub || k.hint) && (
        <div className="mt-2 text-[0.65rem] text-nex-muted leading-snug" data-testid={`nds-kpi-hint-${k.id}`}>
          {k.sub && <span className="uppercase tracking-widest font-mono mr-1">{k.sub} ·</span>}{k.hint}
        </div>
      )}
    </div>
  );
}
